import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Plus, Trash2 } from 'lucide-react';
import { formatMoney } from '@erp/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Field, Input, Select } from '@/components/ui/input';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ui/use-confirm';

/**
 * Pagos de una factura de compra: cuánto se ha pagado, cuánto queda y el alta de un pago nuevo.
 *
 * El importe se manda en positivo: el servidor lo apunta como salida de la cuenta y rechaza pasarse
 * del total. Aquí no se calcula nada que el servidor no vuelva a comprobar.
 */

interface Pago {
  id: string; amount: number; paidAt: string; accountAlias: string;
  methodName: string | null; notes: string | null;
}

interface Cuenta { id: string; alias: string; saldo: number; active: boolean; isDefaultPayments: boolean }
interface Forma { id: string; name: string }

export function PaymentsPanel({ invoiceId, total }: { invoiceId: string; total: number }) {
  const { can } = useAuth();
  const puedePagar = can('finance.write');
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [abierto, setAbierto] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [methodId, setMethodId] = useState('');
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);

  const pagosQuery = useQuery({
    queryKey: ['invoice-payments', invoiceId],
    queryFn: () => api<{ data: Pago[]; total: number; totalPagado: number; pendiente: number }>(
      `/purchase-invoices/${invoiceId}/payments`
    )
  });
  const cuentasQuery = useQuery({
    queryKey: ['treasury', 'accounts'],
    queryFn: () => api<{ data: Cuenta[] }>('/bank-accounts'),
    enabled: puedePagar
  });
  const formasQuery = useQuery({
    queryKey: ['payment-methods', 'pagos'],
    queryFn: () => api<{ data: Forma[] }>('/payment-methods?scope=pagos'),
    enabled: puedePagar,
    staleTime: 10 * 60 * 1000
  });

  const cuentas = (cuentasQuery.data?.data ?? []).filter((cuenta) => cuenta.active);
  const formas = formasQuery.data?.data ?? [];
  const pagado = pagosQuery.data?.totalPagado ?? 0;
  const pendiente = pagosQuery.data?.pendiente ?? Math.round((total - pagado) * 100) / 100;

  const refrescar = async () => {
    await qc.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] });
    await qc.invalidateQueries({ queryKey: ['treasury'] });
  };

  const abrirAlta = () => {
    const porDefecto = cuentas.find((cuenta) => cuenta.isDefaultPayments) ?? cuentas[0];
    setAccountId(porDefecto?.id ?? '');
    setMethodId('');
    setAmount(pendiente > 0 ? pendiente.toFixed(2) : '');
    setPaidAt(new Date().toISOString().slice(0, 10));
    setNotas('');
    setAbierto(true);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api(`/purchase-invoices/${invoiceId}/payments`, {
        method: 'POST',
        body: {
          accountId,
          methodId: methodId || null,
          amount: Number(amount.replace(',', '.')),
          paidAt,
          notes: notas || null
        }
      });
      toast.success('Pago registrado');
      setAbierto(false);
      await refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo registrar el pago');
    } finally {
      setGuardando(false);
    }
  };

  const anular = async (id: string) => {
    const sigue = await confirm({
      title: '¿Anular el pago?',
      description: 'Se borra el pago y su movimiento de banco: el saldo de la cuenta vuelve atrás.',
      confirmLabel: 'Anular pago',
      destructive: true
    });
    if (!sigue) return;
    try {
      await api(`/purchase-invoice-payments/${id}`, { method: 'DELETE' });
      toast.success('Pago anulado');
      await refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo anular el pago');
    }
  };

  return (
    <div className="rounded-lg border border-border">
      {dialog}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex items-center gap-2">
          <Banknote className="size-4 text-muted-foreground" />
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pagos</p>
          {pendiente <= 0.004
            ? <Badge tone="success">Pagada</Badge>
            : pagado > 0
              ? <Badge tone="warning">Parcial · quedan {formatMoney(pendiente)}</Badge>
              : <Badge tone="neutral">Sin pagos</Badge>}
        </div>
        {puedePagar && pendiente > 0.004 ? (
          <Button variant="outline" size="sm" onClick={abrirAlta}><Plus className="mr-1.5 size-3.5" /> Registrar pago</Button>
        ) : null}
      </div>

      {pagosQuery.isError ? (
        <p className="px-3 py-3 text-xs text-destructive">No se pudieron cargar los pagos de esta factura.</p>
      ) : pagosQuery.isLoading ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">Cargando pagos…</p>
      ) : (pagosQuery.data?.data.length ?? 0) === 0 && !abierto ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">
          Sin pagos registrados{puedePagar ? ': al registrar uno se descuenta de la cuenta de la empresa.' : '.'}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {(pagosQuery.data?.data ?? []).map((pago) => (
            <li key={pago.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
              <div>
                <p className="font-medium">{formatMoney(pago.amount)} · {pago.paidAt}</p>
                <p className="text-[11px] text-muted-foreground">
                  {pago.accountAlias}{pago.methodName ? ` · ${pago.methodName}` : ''}{pago.notes ? ` · ${pago.notes}` : ''}
                </p>
              </div>
              {puedePagar ? (
                <Button variant="ghost" size="icon-sm" title="Anular pago" onClick={() => void anular(pago.id)}>
                  <Trash2 />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {abierto ? (
        <div className="space-y-3 border-t border-border p-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Cuenta" required>
              <Select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">Elige la cuenta</option>
                {cuentas.map((cuenta) => (
                  <option key={cuenta.id} value={cuenta.id}>{cuenta.alias}</option>
                ))}
              </Select>
            </Field>
            <Field label="Importe" required hint={`Quedan ${formatMoney(pendiente)}`}>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <Field label="Fecha" required>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
            </Field>
            <Field label="Forma de pago">
              <Select value={methodId} onChange={(e) => setMethodId(e.target.value)}>
                <option value="">Sin especificar</option>
                {formas.map((forma) => <option key={forma.id} value={forma.id}>{forma.name}</option>)}
              </Select>
            </Field>
            <Field label="Notas" className="sm:col-span-2">
              <Input value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Referencia de la transferencia…" />
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}>Cancelar</Button>
            <Button size="sm" disabled={guardando || !accountId || !amount} onClick={() => void guardar()}>Registrar</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
